/**
 * Settings API — base currency, exchange rates.
 */

import { apiFetch } from './fetch';
import { API } from '../constants';
import type { CurrencyRate, ConversionRate } from '../types/dvms.types';

/** Currency codes the backend accepts — the single source for every currency dropdown. */
export async function getSupportedCurrencies(): Promise<string[]> {
    const resp = await apiFetch<{ currencies: string[] }>(API.SETTINGS_SUPPORTED_CURRENCIES);
    return resp.currencies;
}

export async function getBaseCurrency(): Promise<string> {
    const resp = await apiFetch<{ code: string }>(API.SETTINGS_BASE_CURRENCY);
    return resp.code;
}

export async function setBaseCurrency(code: string): Promise<void> {
    await apiFetch(API.SETTINGS_BASE_CURRENCY, {
        method: 'PUT',
        body: JSON.stringify({ code }),
    });
}

export async function getCurrencyRates(): Promise<CurrencyRate[]> {
    return apiFetch<CurrencyRate[]>(API.SETTINGS_CURRENCY_RATES);
}

/** Fetch conversion rates from a base currency to all configured currencies. */
export async function getConversionRates(baseCurrency: string): Promise<ConversionRate[]> {
    return apiFetch<ConversionRate[]>(`${API.SETTINGS_CURRENCY_RATES}?base=${encodeURIComponent(baseCurrency)}`);
}

/** Fetch a single conversion rate between two currencies. */
export async function getConversionRate(baseCurrency: string, targetCurrency: string): Promise<ConversionRate> {
    return apiFetch<ConversionRate>(
        `${API.SETTINGS_CURRENCY_RATES}?base=${encodeURIComponent(baseCurrency)}&target=${encodeURIComponent(targetCurrency)}`,
    );
}

export async function setCurrencyRates(rates: CurrencyRate[]): Promise<void> {
    await apiFetch(API.SETTINGS_CURRENCY_RATES, {
        method: 'PUT',
        body: JSON.stringify(rates),
    });
}

export async function fetchLiveRates(): Promise<CurrencyRate[]> {
    return apiFetch<CurrencyRate[]>(API.SETTINGS_CURRENCY_FETCH, {
        method: 'POST',
    });
}

export async function exportCurrencyRatesCsv(): Promise<{ url: string; count: number }> {
    return apiFetch<{ url: string; count: number }>(API.SETTINGS_CURRENCY_CSV);
}
